'use client';

import type { ReactNode } from 'react';
import { type JSX } from 'react';

import { DateTime } from 'luxon';

import Body from 'DesignSystem3/Text/Body';
import { useExternalSourceDelayed } from 'queries/externalSource';
import { toTimeStringWithTz } from 'utils/time';

import CountdownTimer from '../components/CountdownTimer';
import ROIPill from '../components/ROIPill';
import { useGraphHeaderInputs } from '../interaction/GraphInteractionContext';

interface GraphHeaderShellProps {
    /** Main projection content (chance/forecast label, market tabs, etc.). */
    projection?: JSX.Element;
    /** Inline element next to the projection (e.g. badge, share button). */
    projectionSuffix?: JSX.Element;
    /** Helper text rendered below the projection (e.g. period label). */
    projectionSubtext?: JSX.Element;
    /** Maturity timestamp for delayed-data warning. */
    projectionMaturityMs?: number;
    /** The live chart ran out of samples before the generic age threshold. */
    isProjectionDelayed?: boolean;
    /** Optional toggle/widget rendered to the right of the projection. */
    rightSlot?: ReactNode;
}

/**
 * Presentational shell for the chart header. Reads cross-cutting inputs
 * from `GraphInteractionContext` and lays out projection / countdown / ROI
 * pill / delayed-data warning. See `GraphHeaderInputs.chartHidden` for the
 * ROI-survives-chart-hide invariant.
 *
 * The Kalshi logo lives in the toggle row (`EventChartToggleRow` /
 * `KnockoutChartToggleRow`) above the chart, not inside this header.
 */
const GraphHeaderShellComponent = ({
    projection,
    projectionSuffix,
    projectionSubtext,
    projectionMaturityMs,
    isProjectionDelayed = false,
    rightSlot,
}: GraphHeaderShellProps): JSX.Element | null => {
    const { totalReturnInDollars, onPositionClick, highlightedCloseDate, chartHidden } = useGraphHeaderInputs();

    // Above the early return, as hook order has to hold on every render. The
    // debounced form, not the bare predicate: this warning is a line of text that
    // reflows the header, so a condition that flips with each price tick — an age
    // check on a maturity hovering near its threshold, or a playhead hold that
    // clears on the next frame — strobes it. Both inputs go through the debounce
    // for that reason, which does cost the feed-hold signal its immediacy: it now
    // reads out ~5s into a hold rather than at the first held frame.
    const projectionDelayedMaturity = useExternalSourceDelayed(projectionMaturityMs, {
        enabled: !chartHidden && !!projection,
        feedReportsDelayed: isProjectionDelayed,
    });

    const totalReturnsDefined = totalReturnInDollars !== undefined;
    const chartRowVisible = !chartHidden && (!!projection || !!highlightedCloseDate);

    if (!chartRowVisible && !totalReturnsDefined) return null;

    const projectionMaturityFormatted =
        projectionDelayedMaturity && projectionMaturityMs
            ? toTimeStringWithTz(DateTime.fromMillis(projectionMaturityMs).setZone('America/New_York'))
            : '';

    return (
        <div className="flex">
            <div className="flex flex-col w-full h-full">
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                        <div className="flex items-center sm:flex-col sm:items-start sm:gap-1">
                            {!chartHidden && (
                                <div className="flex items-center">
                                    {projection}
                                    {projectionSuffix}
                                </div>
                            )}
                            {totalReturnsDefined && (
                                <ROIPill totalReturnInDollars={totalReturnInDollars} onClick={onPositionClick} />
                            )}
                        </div>
                    </div>
                    {!chartHidden && (
                        <div className="flex items-center">
                            {rightSlot}
                            {!!highlightedCloseDate && (
                                <div className="flex h-full ml-3">
                                    <CountdownTimer closeDate={highlightedCloseDate} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {!chartHidden && !!projectionSubtext && (
                    <div className="flex items-center pt-1">{projectionSubtext}</div>
                )}
                {projectionDelayedMaturity && (
                    <div className="flex items-center pt-1.5 pb-1.5">
                        <Body.x10 className="text-text-x20">
                            {projectionMaturityFormatted && `As of ${projectionMaturityFormatted} · `}
                            Price data may be delayed
                        </Body.x10>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GraphHeaderShellComponent;
